// Двухуровневая структура левого меню (с 25.06). Верхний уровень — одиночный
// Dashboard + группы; внутри групп пункты фильтруются по роли. Утилитарные
// разделы (новости/база знаний/обратная связь) живут плоско внизу (выбор 3-B).
import type { ReactNode } from 'react'
import { BANK_VIEW_ROLES, CRM_CORE_ROLES, CUSTOMS_VIEW_ROLES, isCrmCoreRole } from '@crm/shared'
import {
  IconDashboard,
  IconPlanning,
  IconAnalytics,
  IconClients,
  IconRequests,
  IconOrders,
  IconProcurement,
  IconQueue,
  IconSupplierRequest,
  IconSuppliers,
  IconGauge,
  IconTable,
  IconBrands,
  IconExchangeRates,
  IconAccounts,
  IconDatabase,
  IconFeedback,
  IconCatalog,
  IconKnowledgeBase,
  IconNews,
  IconSales,
  IconLibrary,
  IconShield,
  IconLogistics,
  IconCustoms,
  IconBrokerRequests,
  IconBrokers,
  IconBank,
} from './sidebarIcons'

export interface NavLeaf {
  to: string
  label: string
  /** Кому виден пункт. Нет списка — ролям CRM до этапа 3 (см. leafVisible). */
  roles?: string[]
  icon: ReactNode
  /**
   * Подсвечивать пункт только на своём адресе, без вложенных: у «Нетарифки»
   * (`/customs`) внутри лежат «Запросы брокеру» (`/customs/requests`).
   */
  end?: boolean
}

export interface NavGroup {
  id: string
  label: string
  icon: ReactNode
  children: NavLeaf[]
}

// Одиночный пункт верхнего уровня — всегда первым, без группы.
export const NAV_TOP: NavLeaf = { to: '/dashboard', label: 'Dashboard', icon: <IconDashboard /> }

export const NAV_GROUPS: NavGroup[] = [
  {
    id: 'sales',
    label: 'Продажи',
    icon: <IconSales />,
    children: [
      {
        to: '/clients',
        label: 'Клиенты',
        // Бухгалтер — только просмотр (этап 3, Б1).
        roles: ['ADMIN', 'GENERAL_DIRECTOR', 'MANAGER', 'ROP', 'ASSISTANT', 'ACCOUNTANT'],
        icon: <IconClients />,
      },
      {
        to: '/requests',
        label: 'Заявки',
        roles: ['ADMIN', 'GENERAL_DIRECTOR', 'MANAGER', 'PROCUREMENT', 'ROP', 'ROS', 'ASSISTANT'],
        icon: <IconRequests />,
      },
      {
        to: '/orders',
        label: 'Заказы',
        // Бухгалтер — только просмотр (этап 3, Б1).
        roles: [
          'ADMIN',
          'GENERAL_DIRECTOR',
          'MANAGER',
          'PROCUREMENT',
          'ROP',
          'ROS',
          'ASSISTANT',
          'ACCOUNTANT',
        ],
        icon: <IconOrders />,
      },
      {
        // Банковские выписки и поступления от клиентов (этап 3, Б2).
        to: '/bank',
        label: 'Банк',
        roles: [...BANK_VIEW_ROLES],
        icon: <IconBank />,
      },
    ],
  },
  {
    id: 'procurement',
    label: 'Закупки',
    icon: <IconProcurement />,
    children: [
      {
        to: '/procurement/queue',
        label: 'Очередь квот',
        // Без ADMIN (решение Сергея 18.09.2026, п. 11 встречи 17.09): раздел
        // заказчику-админу не нужен, API отдаёт ему 403.
        roles: ['GENERAL_DIRECTOR', 'PROCUREMENT', 'ROS'],
        icon: <IconQueue />,
      },
      {
        to: '/procurement-requests',
        label: 'Запросы поставщикам',
        roles: ['ADMIN', 'PROCUREMENT', 'ROS'],
        icon: <IconSupplierRequest />,
      },
      {
        to: '/suppliers',
        label: 'Поставщики',
        // РОП (с 29.09.2026): видит всех, правит только поставщиков РФ.
        roles: ['ADMIN', 'GENERAL_DIRECTOR', 'PROCUREMENT', 'ROS', 'ROP'],
        icon: <IconSuppliers />,
      },
      {
        // Дашборд РОЗ (этап 3): у РОЗ он же на главной (/dashboard); прямой
        // пункт нужен ADMIN/ГД, у которых на главной админский дашборд.
        to: '/dashboard/ros',
        label: 'Дашборд РОЗ',
        roles: ['ADMIN', 'GENERAL_DIRECTOR', 'ROS'],
        icon: <IconGauge />,
      },
    ],
  },
  {
    // Логист (Л1–Л3, 25.09.2026, docs/features/nontariff.md): нетарифка позиций из-за
    // рубежа и обмен файлами с брокером. Правит только логист; ADMIN, ГД и РОЗ — просмотр.
    id: 'logistics',
    label: 'Логистика',
    icon: <IconLogistics />,
    children: [
      {
        to: '/customs',
        label: 'Нетарифка',
        roles: [...CUSTOMS_VIEW_ROLES],
        icon: <IconCustoms />,
        end: true,
      },
      {
        to: '/customs/requests',
        label: 'Запросы брокеру',
        roles: [...CUSTOMS_VIEW_ROLES],
        icon: <IconBrokerRequests />,
      },
      {
        // Справочник брокеров (27.09.2026): кому уходят файлы нетарифки.
        to: '/customs/brokers',
        label: 'Брокеры',
        roles: [...CUSTOMS_VIEW_ROLES],
        icon: <IconBrokers />,
      },
    ],
  },
  {
    id: 'analytics',
    label: 'Аналитика',
    icon: <IconAnalytics />,
    children: [
      {
        to: '/analytics',
        label: 'Аналитика',
        roles: ['ADMIN', 'GENERAL_DIRECTOR', 'ROP', 'ROS'],
        icon: <IconAnalytics />,
      },
      {
        to: '/planning',
        label: 'Планирование',
        // Бухгалтер вносит факт продаж, когда Павла нет (этап 3, C2). Помощник —
        // только просмотр своих менеджеров (29.09.2026).
        roles: ['ADMIN', 'GENERAL_DIRECTOR', 'ROP', 'ACCOUNTANT', 'ASSISTANT'],
        icon: <IconPlanning />,
      },
    ],
  },
  {
    id: 'reference',
    label: 'Справочники',
    icon: <IconLibrary />,
    children: [
      { to: '/catalog', label: 'Каталог', icon: <IconCatalog /> },
      {
        to: '/brands',
        label: 'Бренды',
        roles: ['ADMIN', 'GENERAL_DIRECTOR', 'MANAGER', 'ROP', 'ASSISTANT'],
        icon: <IconBrands />,
      },
      { to: '/table', label: 'Таблица', icon: <IconTable /> },
    ],
  },
  {
    id: 'admin',
    label: 'Администрирование',
    icon: <IconShield />,
    children: [
      {
        to: '/accounts',
        label: 'Аккаунты',
        roles: ['ADMIN', 'GENERAL_DIRECTOR'],
        icon: <IconAccounts />,
      },
      {
        to: '/exchange-rates',
        label: 'Курс валют',
        // Бухгалтер вводит курсы (этап 3, C2).
        roles: ['ADMIN', 'GENERAL_DIRECTOR', 'ACCOUNTANT'],
        icon: <IconExchangeRates />,
      },
      {
        to: '/unf-nomenclature',
        label: 'Номенклатура УНФ',
        // Бухгалтер раз в неделю загружает выгрузку из УНФ — защита от дублей (15.10.2026).
        roles: ['ADMIN', 'GENERAL_DIRECTOR', 'ACCOUNTANT'],
        icon: <IconDatabase />,
      },
      {
        to: '/database',
        label: 'База данных',
        roles: ['ADMIN', 'GENERAL_DIRECTOR'],
        icon: <IconDatabase />,
      },
    ],
  },
]

// Утилитарная зона внизу — без группировки (выбор 3-B).
export const NAV_SECONDARY: NavLeaf[] = [
  {
    to: '/news',
    label: 'Лента новостей',
    // Руководители подразделений этапа 3 публикуют новости, как РОП и РОЗ (25.09.2026).
    roles: ['ADMIN', 'GENERAL_DIRECTOR', 'ROP', 'ROS', 'ACCOUNTANT', 'LOGIST', 'WAREHOUSE_HEAD'],
    icon: <IconNews />,
  },
  { to: '/knowledge-base', label: 'База знаний', icon: <IconKnowledgeBase /> },
  {
    to: '/feedback',
    label: 'Обратная связь',
    // Бухгалтер и логист пишут обращения, как роли CRM (Сергей 19.10.2026); страница и API открыты всем.
    roles: [...CRM_CORE_ROLES, 'ACCOUNTANT', 'LOGIST'],
    icon: <IconFeedback />,
  },
]

/**
 * Виден ли пункт для роли. Белый список: пункт без `roles` — для ролей CRM до
 * этапа 3 (CRM_CORE_ROLES), а не для всех; ролям этапа 3 (бухгалтер, логист,
 * склад) пункт открывается только явным списком `roles` (23.09.2026, C1).
 * «Главная» (NAV_TOP) рисуется всем без этой проверки.
 */
export function leafVisible(leaf: NavLeaf, role: string): boolean {
  return leaf.roles ? leaf.roles.includes(role) : isCrmCoreRole(role)
}
export function visibleChildren(group: NavGroup, role: string): NavLeaf[] {
  return group.children.filter((c) => leafVisible(c, role))
}
